import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AdminPageHeader } from "@/components/admin/crud-helpers";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";
import { MEGA_MENU_ICON_NAMES, resolveMegaMenuIcon } from "@/lib/mega-menu-icons";
import type { MegaMenuConfig, MegaMenuCategory, MegaMenuGroup, MegaMenuItem } from "@/hooks/use-mega-menu";
import { Plus, Trash2, ArrowUp, ArrowDown, Save, RotateCcw, Settings2, ListTree, Star } from "lucide-react";

const MENU_KEY = "marketing";

export const Route = createFileRoute("/_authenticated/admin/marketing-menu")({
  component: MarketingMenuAdmin,
});

const EMPTY: MegaMenuConfig = { label: "Marketing", categories: [] };

function MarketingMenuAdmin() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["mega-menu", MENU_KEY, "admin"],
    queryFn: async () => {
      const { data, error } = await supabase.from("mega_menus").select("config").eq("key", MENU_KEY).maybeSingle();
      if (error) throw error;
      return (data?.config as MegaMenuConfig) ?? EMPTY;
    },
  });

  const [draft, setDraft] = useState<MegaMenuConfig>(EMPTY);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data) {
      setDraft(JSON.parse(JSON.stringify(data)));
      setDirty(false);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: async (cfg: MegaMenuConfig) => {
      const { error } = await supabase
        .from("mega_menus")
        .upsert({ key: MENU_KEY, config: cfg as any, updated_at: new Date().toISOString() });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Marketing menu saved");
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["mega-menu"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Save failed"),
  });

  function update(next: MegaMenuConfig) {
    setDraft(next);
    setDirty(true);
  }

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <AdminPageHeader
          title="Marketing mega menu"
          description="Add, rename, reorder, and re-link categories and sub-items shown in the Marketing dropdown."
        />
        <div className="flex gap-2">
          <Button
            variant="ghost"
            disabled={!dirty || !data}
            onClick={() => data && setDraft(JSON.parse(JSON.stringify(data)))}
          >
            <RotateCcw className="h-4 w-4" /> Revert
          </Button>
          <Button onClick={() => save.mutate(draft)} disabled={!dirty || save.isPending}>
            <Save className="h-4 w-4" /> {save.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="general">
        <PillTabsList search className="flex flex-wrap">
          <PillTabsTrigger value="general"><Settings2 className="mr-1.5 h-4 w-4" />General</PillTabsTrigger>
          <PillTabsTrigger value="categories"><ListTree className="mr-1.5 h-4 w-4" />Categories</PillTabsTrigger>
          <PillTabsTrigger value="featured"><Star className="mr-1.5 h-4 w-4" />Featured panel</PillTabsTrigger>
        </PillTabsList>

        <TabsContent value="general" className="mt-4">
          <div className="rounded-lg border border-border bg-card p-4 space-y-4">
            <div className="grid gap-2 sm:max-w-md">
              <Label>Menu label</Label>
              <Input
                value={draft.label ?? ""}
                onChange={(e) => update({ ...draft, label: e.target.value })}
                placeholder="Marketing"
              />
              <p className="text-xs text-muted-foreground">
                Shown as the top-level link in the public header.
              </p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="categories" className="mt-4 space-y-4">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              {draft.categories.length} categor{draft.categories.length === 1 ? "y" : "ies"} in this menu.
            </p>
            <Button
              variant="outline"
              onClick={() =>
                update({
                  ...draft,
                  categories: [
                    ...draft.categories,
                    { label: "New category", desc: "", icon: "Sparkles", groups: [{ title: "Group", items: [] }] },
                  ],
                })
              }
            >
              <Plus className="h-4 w-4" /> Add category
            </Button>
          </div>

          <Accordion type="multiple" className="space-y-2">
            {draft.categories.map((cat, ci) => (
              <AccordionItem
                key={ci}
                value={`cat-${ci}`}
                className="rounded-lg border border-border bg-card"
              >
                <AccordionTrigger className="px-4 py-3 hover:no-underline">
                  <CategoryHeader cat={cat} index={ci} total={draft.categories.length} />
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <CategoryEditor
                    cat={cat}
                    onChange={(next) => {
                      const cats = [...draft.categories];
                      cats[ci] = next;
                      update({ ...draft, categories: cats });
                    }}
                    onMove={(dir) => {
                      const cats = [...draft.categories];
                      const j = ci + dir;
                      if (j < 0 || j >= cats.length) return;
                      [cats[ci], cats[j]] = [cats[j], cats[ci]];
                      update({ ...draft, categories: cats });
                    }}
                    onDelete={() => {
                      if (!confirm(`Delete category "${cat.label}"?`)) return;
                      const cats = draft.categories.filter((_, k) => k !== ci);
                      update({ ...draft, categories: cats });
                    }}
                  />
                </AccordionContent>
              </AccordionItem>
            ))}
            {draft.categories.length === 0 && (
              <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                No categories yet. Click “Add category” to create the first one.
              </div>
            )}
          </Accordion>
        </TabsContent>

        <TabsContent value="featured" className="mt-4">
          <div className="rounded-lg border border-border bg-card p-4">
            <FeaturedEditor
              featured={draft.featured}
              onChange={(f) => update({ ...draft, featured: f })}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CategoryHeader({ cat, index, total }: { cat: MegaMenuCategory; index: number; total: number }) {
  const Icon = resolveMegaMenuIcon(cat.icon);
  const itemCount = (cat.groups ?? []).reduce((n, g) => n + (g.items?.length ?? 0), 0);
  return (
    <div className="flex w-full items-center gap-3 text-left">
      <Icon className="h-4 w-4 text-primary" />
      <span className="font-medium">{cat.label || "(untitled)"}</span>
      <span className="text-xs text-muted-foreground">
        · {cat.groups?.length ?? 0} groups · {itemCount} items
      </span>
      <span className="ml-auto text-xs text-muted-foreground">
        {index + 1} / {total}
      </span>
    </div>
  );
}

function FeaturedEditor({
  featured,
  onChange,
}: {
  featured?: MegaMenuConfig["featured"];
  onChange: (f: MegaMenuConfig["featured"]) => void;
}) {
  const f = featured ?? { title: "", desc: "", to: "" };
  return (
    <div className="grid gap-2">
      <Label>Featured panel (optional)</Label>
      <div className="grid gap-2 sm:grid-cols-3">
        <Input placeholder="Title" value={f.title} onChange={(e) => onChange({ ...f, title: e.target.value })} />
        <Input placeholder="Description" value={f.desc} onChange={(e) => onChange({ ...f, desc: e.target.value })} />
        <Input placeholder="/contact" value={f.to} onChange={(e) => onChange({ ...f, to: e.target.value })} />
      </div>
    </div>
  );
}

function CategoryEditor({
  cat,
  onChange,
  onMove,
  onDelete,
}: {
  cat: MegaMenuCategory;
  onChange: (next: MegaMenuCategory) => void;
  onMove: (dir: -1 | 1) => void;
  onDelete: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
        <div className="grid gap-1.5">
          <Label className="text-xs">Label</Label>
          <Input value={cat.label} onChange={(e) => onChange({ ...cat, label: e.target.value })} />
        </div>
        <div className="grid gap-1.5">
          <Label className="text-xs">Icon</Label>
          <IconSelect value={cat.icon} onChange={(v) => onChange({ ...cat, icon: v })} />
        </div>
        <div className="flex items-end gap-1">
          <Button size="sm" variant="ghost" onClick={() => onMove(-1)} title="Move up">
            <ArrowUp className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onMove(1)} title="Move down">
            <ArrowDown className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete} title="Delete">
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>
      <div className="grid gap-1.5">
        <Label className="text-xs">Description</Label>
        <Textarea
          rows={2}
          value={cat.desc}
          onChange={(e) => onChange({ ...cat, desc: e.target.value })}
        />
      </div>

      <div className="space-y-3">
        {(cat.groups ?? []).map((g, gi) => (
          <GroupEditor
            key={gi}
            group={g}
            onChange={(ng) => {
              const groups = [...cat.groups];
              groups[gi] = ng;
              onChange({ ...cat, groups });
            }}
            onMove={(dir) => {
              const groups = [...cat.groups];
              const j = gi + dir;
              if (j < 0 || j >= groups.length) return;
              [groups[gi], groups[j]] = [groups[j], groups[gi]];
              onChange({ ...cat, groups });
            }}
            onDelete={() => {
              if (!confirm(`Delete group "${g.title}"?`)) return;
              onChange({ ...cat, groups: cat.groups.filter((_, k) => k !== gi) });
            }}
          />
        ))}
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            onChange({
              ...cat,
              groups: [...(cat.groups ?? []), { title: "New group", items: [] }],
            })
          }
        >
          <Plus className="h-4 w-4" /> Add group
        </Button>
      </div>
    </div>
  );
}

function GroupEditor({
  group,
  onChange,
  onMove,
  onDelete,
}: {
  group: MegaMenuGroup;
  onChange: (g: MegaMenuGroup) => void;
  onMove: (dir: -1 | 1) => void;
  onDelete: () => void;
}) {
  return (
    <div className="rounded-md border border-border/60 bg-background/40 p-3 space-y-3">
      <div className="flex items-center gap-2">
        <Input
          className="max-w-xs"
          value={group.title}
          onChange={(e) => onChange({ ...group, title: e.target.value })}
          placeholder="Group title"
        />
        <div className="ml-auto flex gap-1">
          <Button size="sm" variant="ghost" onClick={() => onMove(-1)}>
            <ArrowUp className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onMove(1)}>
            <ArrowDown className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {(group.items ?? []).map((it, ii) => (
          <ItemRow
            key={ii}
            item={it}
            onChange={(ni) => {
              const items = [...group.items];
              items[ii] = ni;
              onChange({ ...group, items });
            }}
            onMove={(dir) => {
              const items = [...group.items];
              const j = ii + dir;
              if (j < 0 || j >= items.length) return;
              [items[ii], items[j]] = [items[j], items[ii]];
              onChange({ ...group, items });
            }}
            onDelete={() => onChange({ ...group, items: group.items.filter((_, k) => k !== ii) })}
          />
        ))}
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            onChange({
              ...group,
              items: [...(group.items ?? []), { label: "New item", to: "/services", icon: "Sparkles" }],
            })
          }
        >
          <Plus className="h-4 w-4" /> Add item
        </Button>
      </div>
    </div>
  );
}

function ItemRow({
  item,
  onChange,
  onMove,
  onDelete,
}: {
  item: MegaMenuItem;
  onChange: (it: MegaMenuItem) => void;
  onMove: (dir: -1 | 1) => void;
  onDelete: () => void;
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-[1fr_1.4fr_180px_auto] items-center">
      <Input
        value={item.label}
        onChange={(e) => onChange({ ...item, label: e.target.value })}
        placeholder="Label"
      />
      <Input
        value={item.to}
        onChange={(e) => onChange({ ...item, to: e.target.value })}
        placeholder="/marketing/local-seo/gmb-setup"
      />
      <IconSelect value={item.icon} onChange={(v) => onChange({ ...item, icon: v })} />
      <div className="flex">
        <Button size="sm" variant="ghost" onClick={() => onMove(-1)}>
          <ArrowUp className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onMove(1)}>
          <ArrowDown className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="ghost" onClick={onDelete}>
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </div>
    </div>
  );
}

function IconSelect({ value, onChange }: { value?: string; onChange: (v: string) => void }) {
  const Icon = resolveMegaMenuIcon(value);
  return (
    <Select value={value ?? "Sparkles"} onValueChange={onChange}>
      <SelectTrigger>
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-primary" />
          <SelectValue placeholder="Pick icon" />
        </div>
      </SelectTrigger>
      <SelectContent className="max-h-72">
        {MEGA_MENU_ICON_NAMES.map((name) => {
          const I = resolveMegaMenuIcon(name);
          return (
            <SelectItem key={name} value={name}>
              <span className="inline-flex items-center gap-2">
                <I className="h-4 w-4" /> {name}
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
